/**
 * ApplicationsListPage
 *
 * Table listing of Forge Applications with search and quick filters.
 */
import { AppWindow, Search } from 'lucide-react';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import forgeService from '../../services/forgeService';
import { ForgeApplicationWithContext } from '../../types';
import { PageLayout } from '../../components/Layout/PageLayout';
import { Badge } from '../../components/Common/badge';
import TableSkeleton from '../../components/Common/TableSkeleton';

const capitalizeFirst = (str?: string | null): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const getStatusBadgeVariant = (status?: string | null): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!status) return 'outline';
  switch (status.toLowerCase()) {
    case 'deployed':
    case 'running':
      return 'default';
    case 'stopped':
    case 'initializing':
    case 'creating':
      return 'secondary';
    case 'error':
    case 'build_failed':
      return 'destructive';
    default:
      return 'outline';
  }
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
};

const formatAppType = (appType?: string): string => {
  if (!appType) return '-';
  return appType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const ApplicationsListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<ForgeApplicationWithContext[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [healthFilter, setHealthFilter] = useState<string>('all');

  useEffect(() => {
    const fetchApps = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await forgeService.listApplicationsWithContext();
        setApplications(data);
      } catch (err: any) {
        console.error('Error loading applications:', err);
        setError(t('global.somethingWentWrong', 'Something went wrong'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchApps();
  }, [t]);

  const statusOptions = useMemo(() => {
    const statuses = new Set<string>();
    applications.forEach((a) => {
      if (a.status) statuses.add(a.status);
    });
    return ['all', ...Array.from(statuses).sort()];
  }, [applications]);

  const healthOptions = useMemo(() => {
    const healths = new Set<string>();
    applications.forEach((a) => {
      if (a.health_status && a.health_status !== 'unknown') healths.add(a.health_status);
    });
    return ['all', ...Array.from(healths).sort()];
  }, [applications]);

  const filteredApplications = useMemo(() => {
    const q = query.toLowerCase().trim();
    let list = applications;
    if (q) {
      list = list.filter(
        (a) =>
          (a.name || '').toLowerCase().includes(q) ||
          (a.use_case_name || '').toLowerCase().includes(q) ||
          (a.cadrage_name || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') {
      list = list.filter((a) => (a.status || '').toLowerCase() === statusFilter.toLowerCase());
    }
    if (healthFilter !== 'all') {
      list = list.filter((a) => (a.health_status || '').toLowerCase() === healthFilter.toLowerCase());
    }
    return list;
  }, [applications, query, statusFilter, healthFilter]);

  const handleAppClick = (appId: string) => {
    navigate(`/applications/${appId}`);
  };

  return (
    <PageLayout
      icon={<AppWindow className="h-6 w-6" />}
      title={t('forge.pages.applicationsList', 'Applications')}
      subtitle={t('forge.pages.applicationsListDescription', 'View and manage all Forge Applications and their linked Use Cases')}
    >
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row md:items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-muted-foreground" />
              </div>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('forge.applications.searchPlaceholder', 'Search by name, use case, framing...')}
                className="w-full pl-10 pr-3 py-2 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">{t('global.status', 'Status')}:</span>
              {statusOptions.map((status) => {
                const count =
                  status === 'all'
                    ? applications.length
                    : applications.filter((a) => (a.status || '').toLowerCase() === status.toLowerCase()).length;
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setStatusFilter(status)}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                      statusFilter === status ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {status === 'all' ? t('global.all') : status} ({count})
                  </button>
                );
              })}
              {healthOptions.length > 1 && (
                <>
                  <span className="text-xs font-medium text-muted-foreground ml-2">{t('forge.application.healthStatus', 'Health')}:</span>
                  {healthOptions.map((health) => {
                    const count =
                      health === 'all'
                        ? applications.length
                        : applications.filter((a) => (a.health_status || '').toLowerCase() === health.toLowerCase()).length;
                    return (
                      <button
                        key={health}
                        type="button"
                        onClick={() => setHealthFilter(health)}
                        className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                          healthFilter === health ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        {health === 'all' ? t('global.all') : health} ({count})
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground">{error}</div>
      )}

      {!error && (
        <div className="rounded-xl border border-border bg-card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            {isLoading ? (
              <TableSkeleton rows={6} cols={6} />
            ) : filteredApplications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {query || statusFilter !== 'all' || healthFilter !== 'all'
                  ? t('global.noResults', 'No results')
                  : t('forge.applications.noApplications', 'No applications found')}
              </div>
            ) : (
              <table className="min-w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('forge.pages.applicationsList', 'Applications')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('global.status', 'Status')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('forge.application.healthStatus', 'Health')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('forge.application.useCase', 'Use Case')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('forge.application.cadrage', 'Solution')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('forge.applications.lastBuild', 'Last Build')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-card">
                  {filteredApplications.map((app) => (
                    <tr
                      key={app.app_id}
                      className="cursor-pointer hover:bg-muted/30"
                      onClick={() => handleAppClick(app.app_id)}
                    >
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-foreground">{capitalizeFirst(app.name)}</span>
                        {app.app_type && (
                          <div className="text-xs text-muted-foreground">{formatAppType(app.app_type)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {app.status && (
                          <Badge variant={getStatusBadgeVariant(app.status)}>{app.status}</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80">
                        {app.health_status && app.health_status !== 'unknown' ? (
                          <span
                            className={
                              app.health_status.toLowerCase() === 'healthy'
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            }
                          >
                            {app.health_status}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80 truncate max-w-[180px]" title={app.use_case_name || ''}>
                        {app.use_case_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80 truncate max-w-[180px]" title={app.cadrage_name || ''}>
                        {app.cadrage_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{formatDate(app.last_build_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ApplicationsListPage;
